/**
 * MarketingArticleEditor — full editor for a single Insights article.
 *
 * Left column: form (metadata + hero + markdown body + FAQs + LinkedIn
 * carousel data). Right column: sticky preview (Field-Notes card +
 * article layout mirroring www.lutrom.co.uk). Bottom bar: save,
 * download ZIP, publish, plus AI helpers.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Upload, Download, Send, Wand2, Loader2, Plus, Trash2,
  Image as ImageIcon, Eye, FileText, Sparkles, CalendarClock, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getMarketingArticle, updateMarketingArticle, publishMarketingArticle,
  uploadMarketingHero, fetchMarketingHeroBlob, fetchMarketingOgBlob,
  fetchMarketingLinkedInPdfBlob, fetchMarketingArtefactsZipBlob,
  getMarketingReference,
  scheduleMarketingArticle, unscheduleMarketingArticle, linkedinStatus,
} from '../../services/api';
import { ensureAccessToken } from '../../services/session';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { formatDate } from '../../utils/date';
import { ContentApiStatus, WarningList } from '../../components/marketing/ContentApiStatus';
import { LinkedInCarouselPreview } from '../../components/marketing/LinkedInCarouselPreview';
import { LinkedInPostDialog } from '../../components/marketing/LinkedInPostDialog';

// Simple markdown → HTML renderer for the preview pane. We deliberately
// keep this minimal (headings, bold/italic, code, lists, paragraphs) so
// it stays server-of-truth-parity with the backend markdown extension.
// The published article is rendered by the marketing site's Next.js
// template, not by this preview.
const mdToHtml = (src) => {
  if (!src) return '';
  const lines = src.replace(/\r/g, '').split('\n');
  const out = [];
  let inUl = false;
  let inOl = false;
  const flush = () => {
    if (inUl) { out.push('</ul>'); inUl = false; }
    if (inOl) { out.push('</ol>'); inOl = false; }
  };
  const inline = (t) => t
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-slate-100 rounded text-xs">$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 underline">$1</a>');
  for (const raw of lines) {
    const l = raw.trimEnd();
    if (!l.trim()) { flush(); continue; }
    if (l.startsWith('## ')) { flush(); out.push(`<h2 class="text-2xl font-display text-white mt-8 mb-3">${inline(l.slice(3))}</h2>`); continue; }
    if (l.startsWith('### ')) { flush(); out.push(`<h3 class="text-xl font-display text-white mt-6 mb-2">${inline(l.slice(4))}</h3>`); continue; }
    if (l.startsWith('# ')) { flush(); out.push(`<h2 class="text-2xl font-display text-white mt-8 mb-3">${inline(l.slice(2))}</h2>`); continue; }
    if (/^\d+\.\s/.test(l)) {
      if (!inOl) { flush(); out.push('<ol class="list-decimal ml-6 space-y-1 text-slate-300">'); inOl = true; }
      out.push(`<li>${inline(l.replace(/^\d+\.\s+/, ''))}</li>`);
      continue;
    }
    if (l.startsWith('- ') || l.startsWith('* ')) {
      if (!inUl) { flush(); out.push('<ul class="list-disc ml-6 space-y-1 text-slate-300">'); inUl = true; }
      out.push(`<li>${inline(l.slice(2))}</li>`);
      continue;
    }
    flush();
    out.push(`<p class="text-slate-300 leading-relaxed mb-4">${inline(l)}</p>`);
  }
  flush();
  return out.join('\n');
};

const readTimeFromBody = (body) => {
  const words = (body || '').trim().split(/\s+/).filter(Boolean).length;
  const mins = Math.max(3, Math.round(words / 220));
  return `${mins} min read`;
};

// SSE streamer — POSTs to the backend AI endpoint and calls `onDelta`
// with each token chunk as it arrives. Uses fetch() + a manual reader
// because axios doesn't expose the streaming body cleanly.
const streamAi = async (path, body, onDelta) => {
  const token = await ensureAccessToken();
  const RAW = process.env.REACT_APP_BACKEND_URL || '';
  const url = `${RAW.replace(/\/+$/, '')}/api${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    throw new Error((await res.text()) || 'AI request failed');
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      // Keep the raw frame — do NOT .trim() here, otherwise leading
      // spaces that Claude emits inside a token (e.g. " Formal") are
      // lost and the next word gets glued to the previous one
      // ("suffice.Formal", "PMresources"). Only strip a trailing \r.
      let frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      if (frame.endsWith('\r')) frame = frame.slice(0, -1);
      if (!frame.startsWith('data:')) continue;
      // Per the SSE spec the parser strips EXACTLY one space after
      // `data:` — every other leading space is meaningful content.
      let payload = frame.slice(5);
      if (payload.startsWith(' ')) payload = payload.slice(1);
      // Sentinels (control frames) are safe to trim.
      const trimmed = payload.trim();
      if (trimmed === '[DONE]') return;
      if (trimmed.startsWith('[ERROR]')) throw new Error(trimmed.replace('[ERROR]', '').trim());
      // Un-escape newlines the server encoded.
      onDelta(payload.replace(/\\n/g, '\n'));
    }
  }
};

const MarketingArticleEditor = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [ref, setRef] = useState({ categories: [], readTimes: [], imagePositions: [], serviceSlugs: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [heroUrl, setHeroUrl] = useState(null);       // blob URL for preview
  const [aiBusy, setAiBusy] = useState(null);         // 'draft' | 'dek' | 'linkedin' | null
  const [dirty, setDirty] = useState(false);
  const [postDialogOpen, setPostDialogOpen] = useState(false);
  // Scheduling dialog state
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleTime, setScheduleTime] = useState(''); // "YYYY-MM-DDTHH:MM"
  const [scheduleLinkedIn, setScheduleLinkedIn] = useState(true);
  const [liState, setLiState] = useState(null);
  const [scheduling, setScheduling] = useState(false);
  const heroInputRef = useRef(null);

  // Load article + reference bundle in parallel.
  useEffect(() => {
    Promise.all([
      getMarketingArticle(articleId),
      getMarketingReference(),
    ])
      .then(([a, r]) => { setArticle(a); setRef(r); })
      .catch(() => toast.error('Could not load article'))
      .finally(() => setLoading(false));
  }, [articleId]);

  // Load hero preview blob whenever heroPresent flips true.
  useEffect(() => {
    if (!article?.heroPresent) { setHeroUrl(null); return; }
    let revoked = false;
    fetchMarketingHeroBlob(articleId)
      .then((blob) => {
        if (revoked) return;
        setHeroUrl(URL.createObjectURL(blob));
      })
      .catch(() => {});
    return () => { revoked = true; if (heroUrl) URL.revokeObjectURL(heroUrl); };
  }, [article?.heroPresent, articleId]); // heroUrl intentionally omitted — cleanup captures the correct one

  const setField = (key, value) => {
    setArticle((a) => ({ ...a, [key]: value }));
    setDirty(true);
  };

  const setCarousel = (key, value) => {
    setArticle((a) => ({
      ...a,
      linkedinCarousel: { ...(a.linkedinCarousel || {}), [key]: value },
    }));
    setDirty(true);
  };

  const save = async () => {
    if (!article) return false;
    setSaving(true);
    try {
      const payload = {
        slug: article.slug,
        title: article.title,
        dek: article.dek,
        category: article.category,
        readTime: article.readTime,
        publishedAt: article.publishedAt,
        imagePosition: article.imagePosition,
        relatedServiceSlugs: article.relatedServiceSlugs,
        faqs: article.faqs,
        bodyMarkdown: article.bodyMarkdown,
        linkedinCarousel: article.linkedinCarousel,
        author: article.author,
        heroAlt: article.heroAlt,
        seoTitle: article.seoTitle,
        seoDescription: article.seoDescription,
        canonicalUrl: article.canonicalUrl,
      };
      const updated = await updateMarketingArticle(articleId, payload);
      setArticle(updated);
      setDirty(false);
      toast.success('Saved');
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    if (dirty) {
      if (!window.confirm('You have unsaved changes. Save then publish?')) return;
      await save();
    }
    setPublishing(true);
    try {
      const res = await publishMarketingArticle(articleId);
      toast.success(
        <div data-testid="publish-result-toast">
          <div className="font-medium">
            Published to content API · v{res.contentVersion}{res.changed ? '' : ' (no content changes)'}
          </div>
          <div className="text-xs mt-1">
            {res.verifiedLive ? 'Verified live on website.' : 'Not yet verified live on website — use Verify live once the site has refreshed.'}
          </div>
          {res.webhook && (
            <div className="text-xs mt-1">
              Website refresh webhook {res.webhook.ok ? 'delivered' : `failed: ${res.webhook.error || res.webhook.status}`}
            </div>
          )}
          {res.warnings?.length > 0 && (
            <div className="mt-2"><WarningList warnings={res.warnings} testid="publish-warnings" /></div>
          )}
        </div>,
        { duration: 8000, closeButton: true, position: 'bottom-right' },
      );
      const fresh = await getMarketingArticle(articleId);
      setArticle(fresh);
    } catch (e) {
      // Surface as much detail as we can so the admin knows *why*
      // publish failed (auth, missing hero, PDF generation crash, etc.)
      // instead of just seeing "Publish failed" with no context.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail
                     || e?.response?.data?.message
                     || (typeof e?.response?.data === 'string' ? e.response.data : null)
                     || e?.message
                     || 'Publish failed';
      const prefix = status ? `Publish failed (${status})` : 'Publish failed';
      toast.error(
        <div>
          <div className="font-medium">{prefix}</div>
          <div className="text-xs mt-1 break-words">{String(detail).slice(0, 500)}</div>
        </div>,
        { duration: 15000, closeButton: true, position: 'bottom-right' },
      );
      // Also log to the console so we can inspect the full response
      // shape in DevTools if the toast text isn't enough.
      console.error('Publish failed:', e?.response || e);
    } finally {
      setPublishing(false);
    }
  };

  // ---- Scheduling helpers ----
  const openScheduleDialog = () => {
    // Prefill with the current schedule, otherwise "now + 1 hour".
    const src = article.scheduledFor ? new Date(article.scheduledFor)
                                     : new Date(Date.now() + 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    setScheduleTime(
      `${src.getFullYear()}-${pad(src.getMonth() + 1)}-${pad(src.getDate())}T${pad(src.getHours())}:${pad(src.getMinutes())}`,
    );
    setScheduleLinkedIn(article.socialTargets?.linkedin !== false);
    setLiState(null);
    linkedinStatus()
      .then((st) => { setLiState(st); if (!st.ready) setScheduleLinkedIn(false); })
      .catch(() => { setLiState({ ready: false, notReadyReason: 'Could not load LinkedIn status.' }); setScheduleLinkedIn(false); });
    setScheduleOpen(true);
  };

  const doSchedule = async () => {
    if (!scheduleTime) { toast.error('Pick a date and time first'); return; }
    if (dirty) { await save(); }
    setScheduling(true);
    try {
      // datetime-local strings are naive — the browser interprets them
      // as local time. Convert to a real ISO string so the server sees
      // the intended UTC instant.
      const dt = new Date(scheduleTime);
      await scheduleMarketingArticle(articleId, {
        scheduledFor: dt.toISOString(),
        socialTargets: { linkedin: scheduleLinkedIn && !!liState?.ready, facebook: false },
      });
      toast.success(`Scheduled for ${dt.toLocaleString()}`);
      const fresh = await getMarketingArticle(articleId);
      setArticle(fresh);
      setScheduleOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduling(false);
    }
  };

  const cancelSchedule = async () => {
    if (!window.confirm('Cancel the scheduled publish? The article goes back to draft.')) return;
    try {
      await unscheduleMarketingArticle(articleId);
      toast.success('Schedule cancelled');
      const fresh = await getMarketingArticle(articleId);
      setArticle(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cancel failed');
    }
  };

  const uploadHero = async (file) => {
    if (!file) return;
    try {
      await uploadMarketingHero(articleId, file);
      toast.success('Hero uploaded + resized');
      const fresh = await getMarketingArticle(articleId);
      setArticle(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    }
  };

  const downloadZip = async () => {
    try {
      const blob = await fetchMarketingArtefactsZipBlob(articleId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lutrom-insights-${article.slug}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload a hero image first');
    }
  };

  const openBlob = async (fetcher, filename) => {
    try {
      const blob = await fetcher(articleId);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke after a delay so the new tab actually loads.
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      if (filename) {
        // no-op — kept for future "save as" behaviour
      }
    } catch (e) {
      const data = e?.response?.data;
      const text = data instanceof Blob ? await data.text().catch(() => '') : '';
      let detail = data?.detail;
      try { detail = detail || JSON.parse(text).detail; } catch { /* not JSON */ }
      toast.error(detail || 'Not available yet');
    }
  };

  // ---- AI helpers ----
  const runAiDraft = async () => {
    setAiBusy('draft');
    let acc = '';
    try {
      await streamAi('/marketing/ai/draft', {
        title: article.title,
        category: article.category,
      }, (delta) => {
        acc += delta;
        setArticle((a) => ({ ...a, bodyMarkdown: acc }));
      });
      setDirty(true);
      // Auto-derive readTime once the draft settles.
      setArticle((a) => ({ ...a, readTime: readTimeFromBody(acc) }));
    } catch (e) {
      toast.error(e.message || 'AI draft failed');
    } finally {
      setAiBusy(null);
    }
  };

  // Cascading generate — kicks off body→dek→slug→FAQs→carousel
  // sequentially. Each step waits for the previous one so downstream
  // prompts have the freshest context to work from. Individual steps
  // manage their own busy indicator; the cascade just orchestrates.
  const runAiEverything = async () => {
    if (!article.title?.trim() && !article.bodyMarkdown?.trim()) {
      toast.error('Write at least a title or a rough body first');
      return;
    }
    try {
      // 1. Body first (title-driven). Skips if body already has content
      //    so we don't clobber an admin's hand-written article.
      if (!article.bodyMarkdown?.trim() && article.title?.trim()) {
        toast.info('Drafting body from title…');
        await runAiDraft();
      }
      // 2. Dek — from title + body head.
      toast.info('Writing dek…');
      await runAiRewrite('dek');
      // 3. Slug — from title only.
      if (article.title?.trim()) {
        toast.info('Generating slug…');
        await runAiRewrite('slug');
      }
      // 4. FAQs — from body.
      toast.info('Producing FAQs…');
      await runAiGenerate('faqs');
      // 5. Carousel — from body.
      toast.info('Composing LinkedIn carousel…');
      await runAiGenerate('carousel');
      toast.success('Everything generated — review and hit save');
    } catch (e) {
      // Individual steps already toast their own error; this is just
      // the safety net for unexpected throws.
      toast.error(e?.message || 'Cascade stopped');
    }
  };

  const runAiRewrite = async (target, opts = {}) => {
    // `opts.source` overrides the default source-text picker. `opts.setter`
    // overrides which field gets the streamed output. Enables the same
    // helper to power title/slug regenerators (source = body) as well as
    // dek regeneration (source = title + body head).
    let source;
    if (opts.source !== undefined) source = opts.source;
    else if (target === 'dek') source = (article.title || '') + '\n\n' + (article.bodyMarkdown || '').slice(0, 800);
    else if (target === 'title') source = (article.dek || '') + '\n\n' + (article.bodyMarkdown || '').slice(0, 1500);
    else if (target === 'slug') source = article.title || '';
    else source = article.bodyMarkdown;
    if (!source?.trim()) { toast.error('Write some content first'); return; }
    setAiBusy(target);
    let acc = '';
    try {
      await streamAi('/marketing/ai/rewrite', { text: source, target }, (delta) => {
        acc += delta;
        if (target === 'dek') setArticle((a) => ({ ...a, dek: acc }));
        else if (target === 'title') setArticle((a) => ({ ...a, title: acc.replace(/^["'\s]+|["'\s]+$/g, '') }));
        else if (target === 'slug') setArticle((a) => ({ ...a, slug: acc.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') }));
      });
      if (target === 'linkedin' || target === 'facebook') {
        try { await navigator.clipboard.writeText(acc); toast.success(`${target} caption copied to clipboard`); }
        catch { toast.success('Caption generated — check console'); console.log(acc); }
      }
      setDirty(true);
    } catch (e) {
      toast.error(e.message || 'AI rewrite failed');
    } finally {
      setAiBusy(null);
    }
  };

  // Structured (non-streaming) generation — returns JSON that we splat
  // into multiple fields at once. Used for FAQs and the LinkedIn carousel
  // where partial output would be unusable.
  const runAiGenerate = async (target) => {
    setAiBusy(target);
    try {
      const res = await fetch(
        `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api/marketing/ai/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${await ensureAccessToken()}`,
          },
          body: JSON.stringify({
            target,
            title: article.title || '',
            dek: article.dek || '',
            bodyMarkdown: article.bodyMarkdown || '',
          }),
        },
      );
      if (!res.ok) throw new Error((await res.text()) || 'AI generate failed');
      const { result } = await res.json();
      if (target === 'faqs') {
        // Replace the existing FAQ list wholesale — safest UX; the admin
        // can hand-edit / trim from there.
        const rows = (Array.isArray(result) ? result : []).map(f => ({ q: f.q || '', a: f.a || '' }));
        setField('faqs', rows);
        toast.success(`Generated ${rows.length} FAQs`);
      } else if (target === 'carousel') {
        // Backend returns `slide2_items` as `[{tag, head, body}, ...]`
        // and `slide3_items` as `[{tag, body}, ...]` (semantically nice
        // JSON), but the `TupleList` component down the tree indexes
        // each row by numeric column (`row[0]`, `row[1]`, ...). Convert
        // to the tuple shape here so both worlds are happy without
        // touching the shared component or the prompt schema.
        const normalised = { ...(result || {}) };
        if (Array.isArray(normalised.slide2_items)) {
          normalised.slide2_items = normalised.slide2_items.map((it) =>
            Array.isArray(it) ? it : [it.tag || '', it.head || '', it.body || ''],
          );
        }
        if (Array.isArray(normalised.slide3_items)) {
          normalised.slide3_items = normalised.slide3_items.map((it) =>
            Array.isArray(it) ? it : [it.tag || '', it.body || ''],
          );
        }
        // Merge into existing carousel so any manually-edited fields
        // aren't clobbered if Claude omitted them.
        setField('linkedinCarousel', { ...(article.linkedinCarousel || {}), ...normalised });
        toast.success('LinkedIn carousel content generated');
      }
      setDirty(true);
    } catch (e) {
      toast.error(e.message || 'AI generate failed');
    } finally {
      setAiBusy(null);
    }
  };

  // ---- FAQ + carousel item helpers ----
  const addFaq = () => setField('faqs', [...(article.faqs || []), { q: '', a: '' }]);
  const setFaq = (i, key, val) => setField('faqs', article.faqs.map((f, idx) => idx === i ? { ...f, [key]: val } : f));
  const removeFaq = (i) => setField('faqs', article.faqs.filter((_, idx) => idx !== i));

  const carousel = article?.linkedinCarousel || {};
  const setSlide2Items = (items) => setCarousel('slide2_items', items);
  const setSlide3Items = (items) => setCarousel('slide3_items', items);

  const previewHtml = useMemo(() => mdToHtml(article?.bodyMarkdown || ''), [article?.bodyMarkdown]);

  if (loading || !article) {
    return (
      <div className="p-12 text-center text-slate-500">
        <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" /> Loading article…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="marketing-article-editor">
      <PageHeader
        icon={FileText}
        title={article.title || 'Untitled article'}
        subtitle={`/insights/${article.slug}  ·  ${article.status || 'draft'}`}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/admin/marketing/articles')}>
              <ArrowLeft className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">All articles</span>
            </Button>
            <Button
              onClick={runAiEverything}
              disabled={aiBusy !== null || (!article.title?.trim() && !article.bodyMarkdown?.trim())}
              className="bg-purple-600 hover:bg-purple-700"
              title="Cascade: body → dek → slug → FAQs → carousel"
              data-testid="ai-generate-everything-btn"
            >
              <Sparkles className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Generate everything</span>
            </Button>
            <Button
              variant="outline"
              onClick={downloadZip}
              disabled={!article.heroPresent}
              title={article.heroPresent ? 'Download all 5 artefacts as a ZIP' : 'Upload a hero image first'}
              data-testid="download-zip-btn"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Download ZIP</span>
            </Button>
            <Button
              onClick={save}
              disabled={saving || !dirty}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-btn"
            >
              <Save className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{saving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}</span>
            </Button>
            {article.status === 'scheduled' ? (
              <Button
                variant="outline"
                onClick={cancelSchedule}
                className="border-amber-300 text-amber-700 hover:bg-amber-50"
                data-testid="cancel-schedule-btn"
              >
                <X className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Cancel schedule</span>
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={openScheduleDialog}
                disabled={!article.heroPresent || !article.bodyMarkdown?.trim()}
                title={
                  !article.heroPresent ? 'Upload a hero image first'
                  : !article.bodyMarkdown?.trim() ? 'Add a body first'
                  : 'Auto-publish + auto-post at a chosen date/time'
                }
                data-testid="schedule-btn"
              >
                <CalendarClock className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Schedule</span>
              </Button>
            )}
            <Button
              onClick={publish}
              disabled={publishing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="publish-btn"
              title={
                !article.heroPresent ? 'Upload a hero image first'
                : !article.bodyMarkdown?.trim() ? 'Add a body first'
                : 'Validate + save the public version to the content API'
              }
            >
              <Send className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{publishing ? 'Publishing…' : 'Publish'}</span>
            </Button>
          </>
        }
      />

      {article.lastScheduleErrors?.length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 p-4" data-testid="schedule-error-banner">
          <div className="text-sm font-semibold text-red-900">
            Scheduled publish failed{article.lastScheduleFailedAt ? ` on ${new Date(article.lastScheduleFailedAt).toLocaleString('en-GB')}` : ''}
            {article.status === 'draft' ? ' — the article was returned to draft.' : ' — it will retry on the next tick.'}
          </div>
          <ul className="list-disc ml-5 mt-2 space-y-1 text-xs text-red-800" data-testid="schedule-error-list">
            {article.lastScheduleErrors.map((e) => <li key={e} className="break-words">{e}</li>)}
          </ul>
          <div className="text-xs text-red-700 mt-2">Fix these issues, save, then publish or schedule again. Use “Check formatting” to re-validate.</div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Editor form — spans 3 of 5 columns on wide screens */}
        <div className="xl:col-span-3 space-y-6">
          {/* Metadata */}
          <Section title="Article metadata">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Title">
                <div className="flex gap-2">
                  <Input
                    value={article.title || ''}
                    onChange={(e) => setField('title', e.target.value)}
                    data-testid="field-title"
                  />
                  <Button
                    variant="outline"
                    onClick={() => runAiRewrite('title')}
                    disabled={aiBusy === 'title' || !article.bodyMarkdown?.trim()}
                    title="Suggest a punchier title from the body"
                    className="shrink-0"
                    data-testid="ai-rewrite-title"
                  >
                    {aiBusy === 'title' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
              </Field>
              <Field label="URL slug" hint="Kebab-case. Locked once published.">
                <div className="flex gap-2">
                  <Input
                    value={article.slug || ''}
                    onChange={(e) => setField('slug', e.target.value)}
                    disabled={article.status === 'published'}
                    data-testid="field-slug"
                  />
                  <Button
                    variant="outline"
                    onClick={() => runAiRewrite('slug')}
                    disabled={aiBusy === 'slug' || article.status === 'published' || !article.title?.trim()}
                    title="Generate a slug from the title"
                    className="shrink-0"
                    data-testid="ai-rewrite-slug"
                  >
                    {aiBusy === 'slug' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
              </Field>
              <Field label="Category">
                <Select value={article.category || ''} onValueChange={(v) => setField('category', v)}>
                  <SelectTrigger data-testid="field-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(ref.categories || []).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Read time">
                <Select value={article.readTime || ''} onValueChange={(v) => setField('readTime', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(ref.readTimes || []).map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Publish date">
                <Input
                  type="date"
                  value={(article.publishedAt || '').slice(0, 10)}
                  onChange={(e) => setField('publishedAt', e.target.value)}
                />
              </Field>
              <Field label="Author" hint="Shown on the website. Defaults to Lutrom Engineering.">
                <Input
                  value={article.author ?? ''}
                  placeholder="Lutrom Engineering"
                  onChange={(e) => setField('author', e.target.value)}
                  data-testid="field-author"
                />
              </Field>
              <Field label="Hero image position">
                <Select value={article.imagePosition || ''} onValueChange={(v) => setField('imagePosition', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(ref.imagePositions || []).map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-4">
              <Field label="Dek (subtitle)" hint="1-2 sentences under the H1. Auto-used as meta description.">
                <div className="flex gap-2">
                  <Textarea
                    value={article.dek || ''}
                    onChange={(e) => setField('dek', e.target.value)}
                    rows={2}
                    data-testid="field-dek"
                  />
                  <Button
                    variant="outline"
                    onClick={() => runAiRewrite('dek')}
                    disabled={aiBusy === 'dek' || !article.bodyMarkdown?.trim()}
                    title="Regenerate the dek from the body via Claude"
                    className="shrink-0"
                    data-testid="ai-rewrite-dek"
                  >
                    {aiBusy === 'dek' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  </Button>
                </div>
              </Field>
            </div>
          </Section>

          {/* Hero image */}
          <Section title="Hero image" subtitle="1920px longest edge, resized on upload. JPEG/PNG/WebP/HEIC accepted.">
            <div className="flex items-center gap-4">
              <div
                className="w-48 h-28 bg-slate-100 border border-slate-200 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0"
                style={heroUrl ? { backgroundImage: `url(${heroUrl})`, backgroundSize: 'cover', backgroundPosition: article.imagePosition || 'center' } : undefined}
                data-testid="hero-preview"
              >
                {!heroUrl && <ImageIcon className="h-8 w-8 text-slate-300" />}
              </div>
              <div className="flex-1">
                <input
                  ref={heroInputRef}
                  type="file"
                  accept="image/*,image/heic"
                  className="hidden"
                  onChange={(e) => uploadHero(e.target.files?.[0])}
                  data-testid="hero-file-input"
                />
                <Button
                  variant="outline"
                  onClick={() => heroInputRef.current?.click()}
                  data-testid="hero-upload-btn"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  {article.heroPresent ? 'Replace hero' : 'Upload hero'}
                </Button>
                <p className="text-xs text-slate-500 mt-2">
                  {article.heroPresent
                    ? 'Uploaded and cropped to 1920px longest edge.'
                    : 'Landscape 1600×900 or larger works best.'}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <Field label="Hero alt text" hint="Required to publish. Describe the image for screen readers and search.">
                <Input
                  value={article.heroAlt || ''}
                  onChange={(e) => setField('heroAlt', e.target.value)}
                  placeholder="e.g. Engineer thermal-imaging a distribution board"
                  data-testid="field-hero-alt"
                />
              </Field>
            </div>
          </Section>

          {/* Content API + website status */}
          <Section title="Website publishing" subtitle="The website reads published articles from the public content API.">
            <ContentApiStatus article={article} onChanged={setArticle} />
          </Section>

          {/* SEO */}
          <Section title="SEO" subtitle="Optional overrides — blank fields fall back to title, dek and the website URL.">
            <div className="grid grid-cols-1 gap-4">
              <Field label={`SEO title (${(article.seoTitle || '').length}/60)`}>
                <Input value={article.seoTitle || ''} placeholder={article.title || ''}
                  onChange={(e) => setField('seoTitle', e.target.value)} data-testid="field-seo-title" />
              </Field>
              <Field label={`Meta description (${(article.seoDescription || '').length}/160)`}>
                <Textarea rows={2} value={article.seoDescription || ''} placeholder={article.dek || ''}
                  onChange={(e) => setField('seoDescription', e.target.value)} data-testid="field-seo-description" />
              </Field>
              <Field label="Canonical URL" hint={`Default: https://www.lutrom.co.uk/insights/${article.slug}`}>
                <Input value={article.canonicalUrl || ''} placeholder="https://…"
                  onChange={(e) => setField('canonicalUrl', e.target.value)} data-testid="field-canonical-url" />
              </Field>
            </div>
          </Section>

          {/* Body */}
          <Section
            title="Article body"
            subtitle="Markdown — ## H2 for subheads, `- ` for bullets. AI can draft or polish."
            actions={
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={runAiDraft}
                  disabled={aiBusy === 'draft' || !article.title?.trim()}
                  data-testid="ai-draft-btn"
                >
                  {aiBusy === 'draft'
                    ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting…</>
                    : <><Wand2 className="h-4 w-4 mr-2" /> AI draft from title</>}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => runAiRewrite('linkedin')}
                  disabled={aiBusy === 'linkedin' || !article.bodyMarkdown?.trim()}
                  data-testid="ai-linkedin-btn"
                >
                  {aiBusy === 'linkedin' ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                  LinkedIn caption
                </Button>
              </div>
            }
          >
            <Textarea
              value={article.bodyMarkdown || ''}
              onChange={(e) => setField('bodyMarkdown', e.target.value)}
              rows={22}
              placeholder="## What an EICR really covers&#10;&#10;Under BS 7671..."
              className="font-mono text-sm"
              data-testid="field-body"
            />
            <div className="text-xs text-slate-500 mt-2">
              {(article.bodyMarkdown || '').split(/\s+/).filter(Boolean).length} words · est. {readTimeFromBody(article.bodyMarkdown)}
            </div>
          </Section>

          {/* Related services */}
          <Section title="Related services" subtitle="Shown as cards at the bottom of the article. Multi-select.">
            <div className="flex flex-wrap gap-2">
              {(ref.serviceSlugs || []).map((slug) => {
                const on = (article.relatedServiceSlugs || []).includes(slug);
                return (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => {
                      const cur = new Set(article.relatedServiceSlugs || []);
                      if (on) cur.delete(slug); else cur.add(slug);
                      setField('relatedServiceSlugs', Array.from(cur));
                    }}
                    className={
                      'px-2.5 py-1 rounded-full border text-xs font-medium transition-colors '
                      + (on
                        ? 'bg-emerald-700 border-emerald-700 text-white'
                        : 'bg-white border-slate-300 text-slate-600 hover:border-emerald-500 hover:text-emerald-800')
                    }
                    data-testid={`service-${slug}`}
                  >
                    {slug}
                  </button>
                );
              })}
            </div>
          </Section>

          {/* FAQs */}
          <Section
            title="FAQs"
            subtitle="3-5 items. Render as an accordion under the article body."
            actions={
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => runAiGenerate('faqs')}
                  disabled={aiBusy === 'faqs' || !article.bodyMarkdown?.trim()}
                  title="Replace with 4 AI-generated FAQs from the body"
                  data-testid="ai-generate-faqs"
                >
                  {aiBusy === 'faqs' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                  AI generate
                </Button>
                <Button size="sm" variant="outline" onClick={addFaq} data-testid="faq-add">
                  <Plus className="h-4 w-4 mr-1.5" /> Add FAQ
                </Button>
              </div>
            }
          >
            {(article.faqs || []).length === 0 && (
              <p className="text-sm text-slate-500">No FAQs yet.</p>
            )}
            <div className="space-y-3">
              {(article.faqs || []).map((f, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Input
                      value={f.q || ''}
                      onChange={(e) => setFaq(i, 'q', e.target.value)}
                      placeholder="Question"
                      data-testid={`faq-q-${i}`}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-600"
                      onClick={() => removeFaq(i)}
                      data-testid={`faq-remove-${i}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    value={f.a || ''}
                    onChange={(e) => setFaq(i, 'a', e.target.value)}
                    placeholder="Answer"
                    rows={3}
                    data-testid={`faq-a-${i}`}
                  />
                </div>
              ))}
            </div>
          </Section>

          {/* LinkedIn carousel */}
          <Section
            title="LinkedIn carousel content"
            subtitle="Feeds the 3-slide PDF that gets attached to the LinkedIn post."
            actions={
              <Button
                size="sm"
                variant="outline"
                onClick={() => runAiGenerate('carousel')}
                disabled={aiBusy === 'carousel' || !article.bodyMarkdown?.trim()}
                title="Auto-generate all 8 carousel fields from the body"
                data-testid="ai-generate-carousel"
              >
                {aiBusy === 'carousel' ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1.5" />}
                AI generate all
              </Button>
            }
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Cover hook" hint="\\n = hard line break. Max ~4 short lines.">
                <Textarea
                  value={carousel.cover_hook || ''}
                  onChange={(e) => setCarousel('cover_hook', e.target.value)}
                  rows={4}
                  placeholder={'Your EICR\nis booked.\nNow what?'}
                  data-testid="carousel-cover-hook"
                />
              </Field>
              <Field label="Cover sub-line">
                <Input
                  value={carousel.cover_sub || ''}
                  onChange={(e) => setCarousel('cover_sub', e.target.value)}
                  placeholder="Short sub-sentence"
                />
              </Field>
              <Field label="Slide 2 title">
                <Input
                  value={carousel.slide2_title || ''}
                  onChange={(e) => setCarousel('slide2_title', e.target.value)}
                  placeholder="Four things to prep before they arrive."
                />
              </Field>
              <Field label="Slide 3 title">
                <Input
                  value={carousel.slide3_title || ''}
                  onChange={(e) => setCarousel('slide3_title', e.target.value)}
                  placeholder="Reading the report."
                />
              </Field>
              <Field label="Slide 3 sub">
                <Input
                  value={carousel.slide3_sub || ''}
                  onChange={(e) => setCarousel('slide3_sub', e.target.value)}
                  placeholder="What the observation codes actually mean:"
                />
              </Field>
              <Field label="Slide 3 CTA">
                <Input
                  value={carousel.slide3_cta || ''}
                  onChange={(e) => setCarousel('slide3_cta', e.target.value)}
                  placeholder="Full checklist ↓"
                />
              </Field>
            </div>

            <TupleList
              label="Slide 2 items (tag / head / body)"
              cols={['Tag', 'Head', 'Body']}
              rows={carousel.slide2_items || []}
              onChange={setSlide2Items}
              max={5}
              testidBase="slide2"
            />
            <TupleList
              label="Slide 3 items (tag / body)"
              cols={['Tag', 'Body']}
              rows={carousel.slide3_items || []}
              onChange={setSlide3Items}
              max={5}
              testidBase="slide3"
            />

            <LinkedInCarouselPreview
              articleId={articleId}
              carousel={carousel}
              onFill={(fields) => {
                setArticle((a) => ({ ...a, linkedinCarousel: { ...(a.linkedinCarousel || {}), ...fields } }));
                setDirty(true);
              }}
            />

            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPostDialogOpen(true)}
                data-testid="preview-linkedin-post-btn"
              >
                <Eye className="h-4 w-4 mr-2" /> Preview post
              </Button>
              <Button
                size="sm"
                className="bg-[#0a66c2] hover:bg-[#004182] text-white"
                onClick={() => setPostDialogOpen(true)}
                data-testid="post-linkedin-now-btn"
              >
                <Send className="h-4 w-4 mr-2" /> Post now
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openBlob(fetchMarketingLinkedInPdfBlob)}
                disabled={!article.heroPresent}
                data-testid="preview-linkedin-btn"
              >
                <Eye className="h-4 w-4 mr-2" /> Preview LinkedIn PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => openBlob(fetchMarketingOgBlob)}
                disabled={!article.heroPresent}
                data-testid="preview-og-btn"
              >
                <Eye className="h-4 w-4 mr-2" /> Preview OG share card
              </Button>
            </div>
          </Section>
        </div>

        <LinkedInPostDialog
          open={postDialogOpen}
          onOpenChange={setPostDialogOpen}
          articleId={articleId}
          ensureSaved={async () => (dirty ? save() : true)}
          onPosted={async () => setArticle(await getMarketingArticle(articleId))}
        />

        {/* Preview column */}
        <div className="xl:col-span-2">
          <div className="sticky top-4 space-y-4">
            <Section title="Live preview" subtitle="Mirrors www.lutrom.co.uk/insights">
              <div className="rounded-md overflow-hidden border border-slate-800" style={{ background: '#060b17' }}>
                {/* Field Notes card — matches the index-page grid */}
                <div className="p-4 border-b border-slate-800/60">
                  <div
                    className="aspect-[16/10] rounded-md mb-4 bg-slate-800"
                    style={heroUrl ? {
                      backgroundImage: `url(${heroUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: article.imagePosition || 'center',
                    } : undefined}
                  />
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#3F7DFF] font-semibold mb-2">
                    {article.category || 'INSIGHTS'}
                  </div>
                  <div
                    className="font-display text-white leading-[1.05] uppercase"
                    style={{ fontSize: '1.35rem', letterSpacing: '-0.01em' }}
                  >
                    {article.title || 'Untitled'}
                  </div>
                  {article.dek && (
                    <div className="text-sm text-slate-400 mt-3 line-clamp-3 leading-relaxed">
                      {article.dek}
                    </div>
                  )}
                  <div className="text-[11px] text-slate-500 mt-3 uppercase tracking-wider">
                    {article.publishedAt ? formatDate(new Date(article.publishedAt)) : '—'} · {article.readTime}
                  </div>
                </div>

                {/* Article-page hero + body preview */}
                <div className="relative">
                  {/* Hero backdrop */}
                  <div
                    className="absolute inset-x-0 top-0 h-72 bg-slate-800"
                    style={heroUrl ? {
                      backgroundImage: `linear-gradient(180deg, rgba(6,11,24,0.55) 0%, rgba(6,11,24,0.95) 100%), url(${heroUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: article.imagePosition || 'center',
                    } : {
                      background: 'linear-gradient(180deg, #0a1024 0%, #060b17 100%)',
                    }}
                  />
                  <div className="relative p-6 pt-8">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[#3F7DFF] font-semibold">
                      <span className="inline-block w-8 h-[2px] bg-[#3F7DFF]" />
                      INSIGHTS
                      {article.category && (<><span className="text-slate-500">·</span><span>{(article.category || '').toUpperCase()}</span></>)}
                    </div>
                    <h1
                      className="font-display text-white uppercase mt-3 leading-[1.02]"
                      style={{ fontSize: '1.9rem', letterSpacing: '-0.015em' }}
                    >
                      {article.title || 'Untitled article'}
                    </h1>
                    {article.dek && (
                      <p className="text-slate-300 text-sm mt-4 leading-relaxed">{article.dek}</p>
                    )}
                    <div className="text-[11px] text-slate-500 mt-5 uppercase tracking-wider flex items-center flex-wrap gap-2">
                      <span>Published {article.publishedAt ? formatDate(new Date(article.publishedAt)) : '—'}</span>
                      <span className="text-slate-700">·</span>
                      <span>{article.readTime}</span>
                      <span className="text-slate-700">·</span>
                      <span>By Lutrom Engineering</span>
                    </div>

                    {/* Body */}
                    <div className="mt-8 max-h-[420px] overflow-y-auto pr-2">
                      <div
                        className="text-sm"
                        dangerouslySetInnerHTML={{ __html: previewHtml || '<p class="text-slate-500 italic">Body preview appears here as you type…</p>' }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Schedule dialog */}
      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>Schedule publish</DialogTitle>
            <DialogDescription>
              The article will auto-publish + auto-commit to the marketing repo
              at this time. Social auto-posts fire at the same moment for every
              enabled platform.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                Date &amp; time
              </Label>
              <Input
                type="datetime-local"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                data-testid="schedule-time"
                className="mt-1.5"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Your local time — {Intl.DateTimeFormat().resolvedOptions().timeZone}.
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
                Auto-post to
              </Label>
              <label className="flex items-center gap-2 text-sm py-1.5 cursor-pointer select-none">
                <Checkbox
                  checked={scheduleLinkedIn}
                  onCheckedChange={(v) => setScheduleLinkedIn(!!v)}
                  disabled={!liState?.ready}
                  data-testid="schedule-linkedin"
                />
                <span>LinkedIn Company Page</span>
                <LinkedInStateChip state={liState} />
              </label>
              {liState && !liState.ready && (
                <div className="text-[11px] text-amber-800 ml-6 -mt-0.5 mb-1" data-testid="schedule-linkedin-reason">
                  {liState.notReadyReason} Fix it in Marketing → Settings.
                </div>
              )}
              {liState?.ready && (
                <div className="text-[11px] text-slate-500 ml-6 -mt-0.5 mb-1 font-mono" data-testid="schedule-linkedin-org">
                  Posts PDF carousel to {liState.orgUrn}
                </div>
              )}
              <label className="flex items-center gap-2 text-sm py-1.5 select-none opacity-60">
                <Checkbox checked={false} disabled data-testid="schedule-facebook" />
                <span>Facebook Page</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 ml-1" data-testid="schedule-facebook-disabled">
                  Disabled — integration mocked
                </span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduleOpen(false)} disabled={scheduling}>
              Cancel
            </Button>
            <Button
              onClick={doSchedule}
              disabled={scheduling || !scheduleTime}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="schedule-confirm-btn"
            >
              {scheduling ? 'Scheduling…' : 'Confirm schedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ---- Small local UI helpers ----

const LinkedInStateChip = ({ state }) => {
  const base = 'text-[10px] uppercase tracking-wider rounded px-1.5 py-0.5 ml-1 border';
  if (!state) return <span className={`${base} text-slate-500 bg-slate-50 border-slate-200`} data-testid="schedule-linkedin-state">Checking…</span>;
  if (state.ready) return <span className={`${base} text-emerald-800 bg-emerald-50 border-emerald-200`} data-testid="schedule-linkedin-state">Live — connected</span>;
  return <span className={`${base} text-amber-800 bg-amber-50 border-amber-200`} data-testid="schedule-linkedin-state">Not ready</span>;
};

const Section = ({ title, subtitle, actions, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5">
    <div className="flex items-start justify-between gap-3 mb-4">
      <div>
        <div className="font-display uppercase tracking-wide text-sm text-slate-700">{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {actions}
    </div>
    {children}
  </div>
);

const Field = ({ label, hint, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const TupleList = ({ label, cols, rows, onChange, max, testidBase }) => {
  const width = cols.length;
  const add = () => {
    if ((rows || []).length >= max) return;
    onChange([...(rows || []), Array(width).fill('')]);
  };
  const setCell = (rowIdx, colIdx, v) => {
    onChange((rows || []).map((row, i) => {
      if (i !== rowIdx) return row;
      const next = [...row];
      next[colIdx] = v;
      return next;
    }));
  };
  const remove = (rowIdx) => onChange((rows || []).filter((_, i) => i !== rowIdx));
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
        <Button
          size="sm" variant="outline" onClick={add}
          disabled={(rows || []).length >= max}
          data-testid={`${testidBase}-add`}
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add item
        </Button>
      </div>
      {(rows || []).length === 0 && (
        <p className="text-xs text-slate-500">No items yet.</p>
      )}
      <div className="space-y-2">
        {(rows || []).map((row, i) => (
          <div key={i} className="flex items-start gap-2">
            {cols.map((c, j) => (
              j === 0 ? (
                <Input
                  key={j}
                  value={row[j] || ''}
                  onChange={(e) => setCell(i, j, e.target.value)}
                  placeholder={c}
                  className="w-20 shrink-0"
                  data-testid={`${testidBase}-${i}-${j}`}
                />
              ) : (
                <Input
                  key={j}
                  value={row[j] || ''}
                  onChange={(e) => setCell(i, j, e.target.value)}
                  placeholder={c}
                  data-testid={`${testidBase}-${i}-${j}`}
                />
              )
            ))}
            <Button
              size="sm" variant="ghost" className="text-red-600 shrink-0"
              onClick={() => remove(i)}
              data-testid={`${testidBase}-remove-${i}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MarketingArticleEditor;
